import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../config";
import {
  Dialog,
  DialogPanel,
  DialogBackdrop,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import {
  ClipboardIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";

// http://localhost:5050
// https://cloterenotaire.onrender.com

export default function QuestionnaireAcheteurModal({
  open,
  onClose,
  id_action,
  apiBase = API_BASE,
  title = "Questionnaire acheteur",
}) {
  const [loading, setLoading] = useState(false);
  const [acheteur, setAcheteur] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!open || !id_action) return;

    const ids = Array.isArray(id_action) ? id_action : [id_action];
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const id = ids[0];

        const res = await axios.get(
          `${apiBase}/api/questionnaireAcheteur/${encodeURIComponent(id)}`
        );

        if (cancelled) return;

        const payload =
          res?.data?.payload ??
          res?.data?.payload_json ??
          res?.data?.data?.payload_json ??
          res?.data ??
          null;

        if (!payload?.acheteur) {
          setAcheteur(null);
          return;
        }

        setAcheteur(payload.acheteur);
      } catch (err) {
        console.error("❌ fetch questionnaire acheteur:", err);
        setError("Impossible de charger le questionnaire acheteur");
        setAcheteur(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, id_action, apiBase]);

  const hasValue = (v) =>
    v !== null &&
    v !== undefined &&
    !(typeof v === "string" && String(v).trim() === "");

  const formatValue = (value, key = "") => {
    if (value === true) return "Oui";
    if (value === false) return "Non";
    if (!hasValue(value)) return "Non renseigné";

    if (key === "montantAcquisition") {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        return `${num.toLocaleString("fr-FR")} €`;
      }
    }

    if (key === "superficieM2") {
      return `${value} m²`;
    }

    return String(value);
  };

  const copyToClipboard = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopied(field);
      setTimeout(() => setCopied(null), 1200);
    } catch (err) {
      console.error("copy error:", err);
    }
  };

  const profilRows = useMemo(() => {
    if (!acheteur) return [];
    return [
      {
        key: "typeAcheteur",
        label: "Type d’acheteur",
        value: acheteur.typeAcheteur,
      },
      {
        key: "nbAcquereurs",
        label: "Nombre d’acquéreurs",
        value: acheteur.nbAcquereurs,
      },
    ];
  }, [acheteur]);

  const bienRows = useMemo(() => {
    if (!acheteur) return [];
    return [
      {
        key: "typeBien",
        label: "Type de bien",
        value: acheteur.typeBien,
      },
      {
        key: "etatBien",
        label: "État du bien",
        value: acheteur.etatBien,
      },
      {
        key: "superficieM2",
        label: "Superficie",
        value: acheteur.superficieM2,
      },
    ].filter((row) => hasValue(row.value));
  }, [acheteur]);

  const financementRows = useMemo(() => {
    if (!acheteur) return [];
    return [
      {
        key: "montantAcquisition",
        label: "Montant de l’acquisition",
        value: acheteur.montantAcquisition,
      },
      {
        key: "financement",
        label: "Mode de financement",
        value: acheteur.financement,
      },
      {
        key: "passageCourtier",
        label: "Passage par courtier",
        value: acheteur.passageCourtier,
      },
      {
        key: "banque",
        label: "Banque / courtier",
        value: acheteur?.precisions?.banque,
      },
    ].filter((row) => hasValue(row.value));
  }, [acheteur]);

  const situationRows = useMemo(() => {
    if (!acheteur) return [];
    return [
      {
        key: "residentFiscalFrance",
        label: "Résident fiscal en France",
        value: acheteur.residentFiscalFrance,
      },
      {
        key: "achatViaProcuration",
        label: "Achat par procuration",
        value: acheteur.achatViaProcuration,
      },
      {
        key: "remarque",
        label: "Remarque libre",
        value: acheteur?.precisions?.remarque,
      },
    ].filter((row) => hasValue(row.value));
  }, [acheteur]);

  const sciRows = useMemo(() => {
    if (!acheteur || acheteur.typeAcheteur !== "sci") return [];
    return [
      {
        key: "denomination",
        label: "Dénomination de la SCI",
        value: acheteur?.sci?.denomination,
      },
      {
        key: "representantLegal",
        label: "Nom du représentant légal",
        value: acheteur?.sci?.representantLegal,
      },
    ].filter((row) => hasValue(row.value));
  }, [acheteur]);

  function downloadBlob(content, mime, filename) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  const csvRows = useMemo(() => {
    const rows = [];

    profilRows.forEach((r) =>
      rows.push({
        section: "Profil acheteur",
        label: r.label,
        value: formatValue(r.value, r.key),
      })
    );

    bienRows.forEach((r) =>
      rows.push({
        section: "Bien immobilier",
        label: r.label,
        value: formatValue(r.value, r.key),
      })
    );

    if (sciRows.length) {
      sciRows.forEach((r) =>
        rows.push({
          section: "SCI",
          label: r.label,
          value: formatValue(r.value, r.key),
        })
      );
    }

    financementRows.forEach((r) =>
      rows.push({
        section: "Financement",
        label: r.label,
        value: formatValue(r.value, r.key),
      })
    );

    situationRows.forEach((r) =>
      rows.push({
        section: "Situation administrative",
        label: r.label,
        value: formatValue(r.value, r.key),
      })
    );

    return rows;
  }, [profilRows, bienRows, sciRows, financementRows, situationRows]);

  function toCsv(rows) {
    const esc = (s) => {
      const v = String(s ?? "");
      return /[;\n"]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };

    const lines = [
      ["Section", "Champ", "Valeur"].join(";"),
      ...rows.map((row) =>
        [esc(row.section), esc(row.label), esc(row.value)].join(";")
      ),
    ];

    return lines.join("\n");
  }

  const handleExportCsv = () => {
    const csv = toCsv(csvRows);
    const suffix = Array.isArray(id_action)
      ? `_${id_action.join("-")}`
      : id_action
      ? `_${id_action}`
      : "";

    const now = new Date();
    const iso = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
    ].join("");

    downloadBlob(
      csv,
      "text/csv;charset=utf-8",
      `questionnaire_acheteur${suffix}_${iso}.csv`
    );
  };

  const SectionCard = ({ title, rows }) => {
    if (!rows?.length) return null;

    return (
      <div className="overflow-hidden rounded-xl ring-1 ring-black/5 bg-white">
        <div className="bg-gray-50 px-4 py-2 border-b">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>

        <dl className="divide-y divide-gray-200">
          {rows.map((row) => (
            <div
              key={row.key}
              className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-4 py-3 items-start"
            >
              <dt className="sm:col-span-4 text-sm font-medium text-gray-700">
                {row.label}
              </dt>

              <dd className="sm:col-span-7 text-sm text-black font-bold whitespace-pre-wrap break-words">
                {hasValue(row.value) ? (
                  formatValue(row.value, row.key)
                ) : (
                  <span className="font-normal text-gray-400">
                    Non renseigné
                  </span>
                )}
              </dd>

              <div className="sm:col-span-1 flex sm:justify-end">
                <button
                  type="button"
                  onClick={() =>
                    copyToClipboard(row.key, formatValue(row.value, row.key))
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  title="Copier"
                >
                  {copied === row.key ? (
                    <>
                      <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-600" />
                      Copié
                    </>
                  ) : (
                    <>
                      <ClipboardIcon className="h-4 w-4 text-gray-500" />
                      Copier
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </dl>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />
      <div className="fixed inset-0 p-4 flex items-center justify-center">
        <DialogPanel className="pointer-events-auto w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {title}
            </DialogTitle>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={loading || !acheteur}
                className="btn-primary"
                title="Exporter en CSV"
              >
                Export CSV
              </button>

              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
                title="Fermer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {loading && (
              <div className="rounded-md bg-slate-50 p-4 mb-3">
                <p className="text-sm text-slate-700 m-0">Chargement…</p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-3">
                <p className="text-sm text-red-700 m-0">{error}</p>
              </div>
            )}

            {!loading && !error && !acheteur && (
              <div className="rounded-md bg-slate-50 p-6 text-center">
                <p className="text-sm text-slate-600 m-0">
                  Aucun questionnaire acheteur renseigné.
                </p>
              </div>
            )}

            {!loading && !error && acheteur && (
              <div className="flex flex-col gap-4">
                <SectionCard title="Profil de l’acquéreur" rows={profilRows} />
                <SectionCard title="Bien immobilier" rows={bienRows} />
                <SectionCard title="Financement" rows={financementRows} />
                {sciRows.length > 0 && <SectionCard title="SCI" rows={sciRows} />}
                <SectionCard
                  title="Situation administrative"
                  rows={situationRows}
                />
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}